import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { LayoutDashboard, Columns3, Users, TrendingUp, UserCheck, Settings, LogOut, Search, Bell, Shield, X } from "lucide-react";

const NAV = [
  { to: "/crm", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/crm/pipeline", label: "Pipeline", icon: Columns3 },
  { to: "/crm/leads", label: "Leads", icon: Users },
  { to: "/crm/customers", label: "Customers", icon: UserCheck },
  { to: "/crm/marketing", label: "Marketing", icon: TrendingUp },
  { to: "/crm/settings", label: "Settings", icon: Settings },
];

export default function CrmLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [notifs, setNotifs] = useState([]);
  const [showNotif, setShowNotif] = useState(false);
  const [q, setQ] = useState("");

  const loadNotifs = () => api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  useEffect(() => { loadNotifs(); const t = setInterval(loadNotifs, 30000); return () => clearInterval(t); }, []);
  const unread = notifs.filter((n) => !n.read).length;

  const doSearch = (e) => { e.preventDefault(); if (q.trim()) nav(`/crm/leads?search=${encodeURIComponent(q.trim())}`); };
  const openNotif = () => { setShowNotif(!showNotif); if (!showNotif && unread) { api.post("/notifications/read"); } };

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]">
      <aside className="w-60 shrink-0 bg-[#0B1E36] text-white flex-col hidden md:flex">
        <div className="h-16 flex items-center gap-2 px-6 border-b border-white/10">
          <Shield className="text-[#C5A059]" /><span className="font-display font-bold">Keystone CRM</span>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`crmnav-${n.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${isActive ? "bg-[#C5A059] text-[#0B1E36]" : "text-slate-300 hover:bg-white/10"}`}>
              <n.icon size={18} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2 text-sm"><p className="font-semibold">{user?.name}</p><p className="text-xs text-slate-400 uppercase tracking-wide">{user?.role}</p></div>
          <button onClick={() => { logout(); nav("/crm/login"); }} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:text-white w-full" data-testid="crm-logout"><LogOut size={16} /> Sign out</button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center gap-3 px-4 sticky top-0 z-20">
          <form onSubmit={doSearch} className="flex-1 max-w-md relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, email, policy…" className="w-full h-10 pl-9 pr-3 rounded-sm border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#C5A059] text-sm" data-testid="crm-global-search" />
          </form>
          <div className="relative">
            <button onClick={openNotif} className="relative p-2" data-testid="crm-notifications-btn">
              <Bell size={20} />
              {unread > 0 && <span className="absolute top-0 right-0 bg-[#C5A059] text-[#0B1E36] text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{unread}</span>}
            </button>
            {showNotif && (
              <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-sm shadow-xl z-30 max-h-96 overflow-auto" data-testid="notif-panel">
                <div className="flex items-center justify-between px-4 py-2 border-b"><span className="font-semibold text-sm">Notifications</span><button onClick={() => setShowNotif(false)}><X size={16} /></button></div>
                {notifs.length === 0 ? <p className="p-4 text-sm text-slate-500">No notifications</p> :
                  notifs.map((n) => <button key={n.id} onClick={() => { setShowNotif(false); n.lead_id && nav(`/crm/leads/${n.lead_id}`); }} className="block w-full text-left px-4 py-3 border-b hover:bg-slate-50 text-sm">{n.message}<span className="block text-xs text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</span></button>)}
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
        {/* mobile nav */}
        <nav className="md:hidden grid grid-cols-6 border-t border-slate-200 bg-white sticky bottom-0">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `flex flex-col items-center py-2 text-[10px] ${isActive ? "text-[#C5A059]" : "text-slate-500"}`}><n.icon size={18} />{n.label}</NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
